export enum PickupRequestStatus {
  Waiting = 'Waiting',
  Announced = 'Announced',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

const PICKUP_STATUS_BY_NUMBER: Record<number, PickupRequestStatus> = {
  0: PickupRequestStatus.Waiting,
  1: PickupRequestStatus.Announced,
  2: PickupRequestStatus.Completed,
  3: PickupRequestStatus.Cancelled,
};

const PICKUP_STATUS_VALUES = new Set<string>(
  Object.values(PickupRequestStatus),
);

/**
 * Backend serves `Status` as either the string name ("Announced") via OData
 * or the numeric value (1) via SignalR payloads. Use this type to keep both
 * representations valid and normalize via `normalizePickupRequestStatus`.
 */
export type PickupRequestStatusValue = PickupRequestStatus | number | string;

export function normalizePickupRequestStatus(
  status: PickupRequestStatusValue | null | undefined,
): PickupRequestStatus | null {
  if (status == null) return null;
  if (typeof status === 'number') {
    return PICKUP_STATUS_BY_NUMBER[status] ?? null;
  }
  if (typeof status === 'string' && PICKUP_STATUS_VALUES.has(status)) {
    return status as PickupRequestStatus;
  }
  return null;
}

export interface PickupRequestValidationResult {
  RuleSetsExecuted?: unknown[];
  Errors?: unknown[];
}

export interface PickupRequest {
  PickupRequestId: string;
  TenantId?: string;
  AccessPointId: string;
  GateLabel: string;
  GuardianPersonId: string | null;
  GuardianDisplayName: string | null;
  StudentPersonId: string;
  StudentDisplayName: string;
  // TODO(live-pickup): reativar quando o backend passar a enviar a turma do aluno.
  // StudentClassDisplay: string;
  Status: PickupRequestStatusValue;
  RecognizedOnUtc: string;
  RequestedOnUtc: string;
  CompletedOnUtc: string | null;
  ExpiredOnUtc: string | null;
  CompletedByOperatorPersonId: string | null;
  CompletedByOperatorName: string | null;
  CorrelationId: string | null;
  AddedBy?: string | null;
  AddedOnUtc?: string | null;
  UpdatedBy?: string | null;
  UpdatedOnUtc?: string | null;
  DeletedBy?: string | null;
  DeletedOnUtc?: string | null;
  ValidationResult?: PickupRequestValidationResult;
}

export type PickupRequestRealtimeDto = PickupRequest;

export interface PickupRequestODataResponse {
  '@odata.context'?: string;
  '@odata.count'?: number;
  value: PickupRequest[];
}

export interface CompletePickupPayload {
  completedOnUtc?: string;
  note?: string;
  clientRequestId?: string;
}

export interface CompletePickupResponse {
  PickupRequestId: string;
  Status: PickupRequestStatusValue;
  CompletedOnUtc: string;
}
